import { useContext, useRef, useMemo } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";

import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { format, isToday, isYesterday } from "date-fns";

import { useSearchParams } from "react-router-dom";

import { BsSearch } from "react-icons/bs";

import { 
  InfiniteScrollingList,
  Button,
  StatusBadge
} from "@evos/components";
import Header from "components/Header";
import { useWindowSize } from "@evos/hooks";

import { AuthContext } from "lib/context/auth";
import { CHARGER_LOGS } from "./gql";

import type { 
  ChargerMonitors,
} from "types";

function useAuth() {
  return useContext(AuthContext);
}

function formatStatusMessageTimestamp(isoTimestamp: string | null | undefined): string {
  if (isoTimestamp == null || isoTimestamp === "") return "No timestamp";
  try {
    const d = new Date(isoTimestamp);
    if (Number.isNaN(d.getTime())) return isoTimestamp;
    if (isToday(d)) return `Today, ${format(d, "HH:mm:ss.SS")}`;
    if (isYesterday(d)) return `Yesterday, ${format(d, "HH:mm:ss.SS")}`;
    return format(d, "dd MMM yyyy, HH:mm:ss.SS");
  } catch {
    return isoTimestamp;
  }
}

export default function ChargerStatusMessagesList() {
  const auth = useAuth();
  const darkMode = auth?.darkMode ?? false;
  const windowWidth = useWindowSize().width ?? window.innerWidth;

  const [ searchParams, setSearchParams ] = useSearchParams();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchQueryString = searchParams.get("search") ?? "";

  const queryVariables = useMemo(() => ({
    n: 20,
    after: null,
    ...((searchQueryString.length > 0) && { where: { serial: { like: searchQueryString } } })
  }), [searchQueryString]);

  const { 
    data: chargerStatusMessagesData, 
    networkStatus: chargerStatusMessagesNetworkStatus, 
    error: chargerStatusMessagesError, 
    fetchMore,
    refetch
  } = useQuery<ChargerMonitors>(
    CHARGER_LOGS,
    { 
      variables: queryVariables,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    }
  );

  const chargerStatusMessagesInitialLoading = chargerStatusMessagesNetworkStatus === 1;
  const chargerStatusMessagesFetchMoreLoading = chargerStatusMessagesNetworkStatus === 3;
  const chargerStatusMessagesRefetchLoading = chargerStatusMessagesNetworkStatus === 4;

  const pageInfoObject = chargerStatusMessagesData?.charger_monitors?.charger_status_messages?.pageInfo;

  const chargerStatusMessagesList = chargerStatusMessagesData?.charger_monitors?.charger_status_messages?.edges?.map(d => ({
    serial: d?.node?.serial != null 
      ? <Link to={`/c/${d?.node?.serial}`}>{d?.node?.serial}</Link> 
      : "Unknown serial",
    timestamp: formatStatusMessageTimestamp(d?.node?.receiveTimestamp ?? undefined),
    errorCode: d?.node?.errorCode ?? "No error code provided",
    vendorErrorCode: d?.node?.vendorErrorCode ?? "No vendor error code provided",
    info: d?.node?.info ?? "No info provided",
    status: d?.node?.status != null
      ? <><StatusBadge status={d?.node?.status} showStandardName={true} /> {d?.node?.status}</> : "No status",
  }));

  const handleSearch = (searchString: string | null) => {
    if (searchString != null && searchString.length > 0) {
      setSearchParams({search: searchString });
    } else {
      setSearchParams({});
    }
  };

  const handleFetchMore = (pageInfo: ChargerMonitors["charger_monitors"]["charger_status_messages"]["pageInfo"]) => {
    if (pageInfo?.hasNextPage === true) {
      fetchMore({
        variables: {
          ...queryVariables,
          after: pageInfo.endCursor,
        }
      });
    }
  };
  
  return (
    <div id="Settings">
      <Header />
      <Container>
        <Row className="py-4">
          <Col xs={12} className="mb-2">
            <Row className="pb-1 justify-content-start">
              <Col xs={12} md={6} xl={7} xxl={8} five-xl={9} className="d-flex justify-content-start py-2">
                <h1 className="mb-0" style={{fontSize: "1.6em"}}>Charger Status Messages</h1>
              </Col>
            </Row>
          </Col>
          <Col xs={12}>
            <Row className="mb-3">
              <Col xs={12} lg={5}>
                <InputGroup className="pt-2 pt-sm-0">
                  <Form.Control
                    defaultValue={searchQueryString}
                    placeholder="Filter by serial..."
                    aria-label="Filter by serial..."
                    aria-describedby="basic-search"
                    ref={searchInputRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (searchInputRef?.current?.value != null && searchInputRef.current.value.length > 0) {
                          handleSearch(searchInputRef.current.value);
                        } else {
                          handleSearch(null);
                        }
                      }
                    }}
                  />
                  <Button
                    variant="primary"
                    className="px-3 py-2 fw-semibold"
                    id="button-search"
                    isLoading={chargerStatusMessagesInitialLoading || chargerStatusMessagesFetchMoreLoading}
                    onClick={() => {
                      if (searchInputRef?.current?.value != null && searchInputRef.current.value.length > 0) {
                        handleSearch(searchInputRef.current.value);
                      } else {
                        handleSearch(null);
                      }
                    }}>
                    <BsSearch />
                  </Button>
                </InputGroup>
              </Col>
            </Row>
            <Card className={`pb-3 mt-4 ${darkMode ? "bg-dark" : ""}`} style={{overflow: "auto"}}>
              <div style={{minWidth: "1000px"}}>
                <InfiniteScrollingList
                  data={chargerStatusMessagesList}
                  initialLoading={chargerStatusMessagesInitialLoading}
                  fetchMoreLoading={chargerStatusMessagesFetchMoreLoading}
                  refetchLoading={chargerStatusMessagesRefetchLoading}
                  pageSize={20}
                  error={chargerStatusMessagesError}
                  listItemWidths="0.2fr 0.4fr 0.16fr 0.25fr 0.3fr 0.3fr"
                  listItemHeight={60}
                  listHeight={(windowWidth ?? 0) > 3000 ? 800 : 550}
                  pageInfo={pageInfoObject}
                  windowWidth={windowWidth}
                  fetchMoreData={handleFetchMore}
                  refetchData={(n) => refetch({
                    ...queryVariables,
                    n: Math.max(n, 100)
                  })}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
